import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  AggregatedSubmissionData,
  DigitalCase, DigitalQuestion, DigitalSubmissionAggregation, QuestionCaseSetting, QuestionTypeV2,
} from '../../redux/types';
import NumRangeAggregation from './NumberRangeAggregation';
import SingleChoiceAggregation from './SingleChoiceAggregation';
import MultiChoiceAggregation from './MultiChoiceAggregation';
import StringAggregation from './StringAggregation';
import RatioAggregation from './RatioAggregation';
import MultiChoiceWithTextAggregation from './MultiChoiceWithTextAggregation';
import SingleChoiceWithTextAggregation from './SingleChoiceWithTextAggregation';
import { Colors } from '../utilities/Colors';
import LegendAggregation from '../digitalTrial/LegendAggregation';

type Props = {
  selectedSectionId?: number;
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  aggregatedSubmission?: AggregatedSubmissionData;
  settingsDictionary?: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
};

const SubmissionAggregation: React.FC<Props> = ({
  selectedSectionId,
  digitalCases,
  digitalQuestions,
  aggregatedSubmission,
  settingsDictionary,
}: Props) => {
  if (!selectedSectionId || !aggregatedSubmission
    || Object.keys(aggregatedSubmission).length === 0
    || !digitalQuestions.length || !digitalCases.length) {
    return null;
  }
  const dictionary: Record<number, Record<number, DigitalSubmissionAggregation> | undefined> = {};
  aggregatedSubmission[selectedSectionId]?.forEach((as) => {
    if (as.digitalCaseId !== undefined) {
      dictionary[as.digitalQuestionId] = {
        ...dictionary[as.digitalQuestionId],
        [as.digitalCaseId]: as,
      };
    }
  });

  return (
    <Box>
      <TableContainer>
        <Table sx={{ position: 'relative', overflow: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  backgroundColor: 'white',
                  left: 0,
                  minWidth: 300,
                }}
              >
                Questions
              </TableCell>
              {
                digitalCases.map((dc) => (
                  <TableCell key={dc.id} align="center">{dc.name}</TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              digitalQuestions.map((dq) => (
                <TableRow key={dq.id}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      backgroundColor: 'white',
                      left: 0,
                      fontWeight: 500,
                    }}
                  >
                    {dq.text}
                  </TableCell>
                  {
                    digitalCases.map((dc) => {
                      const dismissed = settingsDictionary?.get(dq.id, dc.id)?.hide;
                      if (dismissed) {
                        return (<TableCell sx={{ bgcolor: Colors.dismissed }} key={dc.id} align="center" />);
                      }
                      return (
                        <TableCell
                          key={dc.id}
                          align="center"
                          sx={{
                            lineBreak: 'strict',
                            minWidth: '155px',
                          }}
                        >
                          {(() => {
                            const aggData = dictionary[dq.id]?.[dc.id];
                            if (aggData?.type === QuestionTypeV2.NumberRange) {
                              return <NumRangeAggregation data={aggData} />;
                            }
                            if (aggData?.type === QuestionTypeV2.SingleChoice) {
                              return <SingleChoiceAggregation data={aggData} />;
                            }
                            if (aggData?.type === QuestionTypeV2.MultiChoice) {
                              return <MultiChoiceAggregation data={aggData} />;
                            }
                            if (aggData?.type === QuestionTypeV2.String) {
                              return <StringAggregation data={aggData} />;
                            }
                            if (aggData?.type === QuestionTypeV2.Ratio) {
                              return <RatioAggregation data={aggData} />;
                            }
                            if (aggData?.type === QuestionTypeV2.MultiChoiceWithText) {
                              return <MultiChoiceWithTextAggregation data={aggData} />;
                            }
                            if (aggData?.type === QuestionTypeV2.SingleChoiceWithText) {
                              return <SingleChoiceWithTextAggregation data={aggData} />;
                            }
                            return '-';
                          })()}
                        </TableCell>
                      );
                    })
                  }
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
      <LegendAggregation />
    </Box>
  );
};

export default SubmissionAggregation;
